import { Deal, DealType } from "src/business/deal";

// A deal's `timeRanges` are "HH:mm-HH:mm" strings; parse one into its bounds.
// Returns null for malformed ranges so callers can skip them.
export const parseTimeRange = (
  range: string,
): { start: string; end: string } | null => {
  const [start, end] = range.split("-");
  if (!start || !end) return null;
  return { start, end };
};

// A grid slot ("HH:mm") belongs to a range when start <= slot < end. Zero-padded
// times compare correctly as plain strings, so no date parsing is needed.
export const slotInRange = (slot: string, range: string): boolean => {
  const parsed = parseTimeRange(range);
  if (!parsed) return false;
  return parsed.start <= slot && slot < parsed.end;
};

/**
 * Resolve the active deal/event covering a slot on the selected date. A deal
 * applies when it is active, its `days` include the date (formatted
 * "YYYY-MM-DD"), and one of its `timeRanges` covers the slot. When both a DEAL
 * and an EVENT apply the EVENT wins, so events are never hidden.
 */
export const findDealForSlot = (
  deals: Deal[],
  selectedDateStr: string,
  slot: string,
): Deal | undefined => {
  const applicable = deals.filter(
    (deal) =>
      deal.active &&
      deal.days.includes(selectedDateStr) &&
      deal.timeRanges.some((range) => slotInRange(slot, range)),
  );
  return (
    applicable.find((deal) => deal.type === DealType.EVENT) ?? applicable[0]
  );
};

/**
 * Offer fields to attach to a booking placed on a deal/event slot. A DEAL fills
 * `dealContent`, an EVENT fills `eventContent` — never both, mirroring the
 * backend's dealContent XOR eventContent invariant. Empty when no offer applies.
 */
export interface SlotOffer {
  dealContent?: string;
  eventContent?: string;
}

export const offerForDeal = (deal: Deal | undefined): SlotOffer => {
  if (!deal) return {};
  return deal.type === DealType.EVENT
    ? { eventContent: deal.content }
    : { dealContent: deal.content };
};
